import React from 'react'
import { ChevronDownIcon } from '@mote/assets/icons'
import { ariaAttr } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'

export interface SelectFieldProps extends HTMLXStyledProps<'select'> {
  invalid?: boolean
}

const SelectField = forwardRef<SelectFieldProps, 'select'>((props, ref) => {
  const { children, placeholder, ...rest } = props

  return (
    <StyledSelectField ref={ref} {...rest} data-focus-visible-disabled=''>
      {placeholder && <option value=''>{placeholder}</option>}
      {children}
    </StyledSelectField>
  )
})

if (__DEV__) {
  SelectField.displayName = 'SelectField'
}

interface RootProps extends Omit<HTMLXStyledProps<'div'>, 'color'> {}

type Option = {
  value: string | number
  text: string
  disabled?: boolean
}

type SelectSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface SelectProps extends Omit<SelectFieldProps, 'size'> {
  size?: SelectSize
  readOnly?: boolean
  currentValue?: string | number
  showChevron?: boolean
  options: Array<Option>
  rootProps?: RootProps
}

export const Select = forwardRef<SelectProps, 'select'>((props, ref) => {
  const {
    className,
    placeholder,
    currentValue,
    options: _options,
    disabled = false,
    invalid = false,
    required = false,
    readOnly = false,
    showChevron = true,
    rootProps = {},
    size = 'md',
    color,
    h,
    minH,
    minHeight,
    ...rest
  } = props

  const options = React.useMemo(
    () =>
      _options.map(({ value, text, disabled }, index) => (
        <option
          key={`select-option--${index}:${text}:${value}`}
          value={value}
          disabled={disabled}
        >
          {text}
        </option>
      )),
    [_options]
  )

  return (
    <Root {...rootProps} color={color} disabled={disabled} $size={size}>
      <SelectField
        {...rest}
        ref={ref}
        h={h}
        minH={minH ?? minHeight}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        invalid={invalid}
        required={required}
        readOnly={readOnly}
        value={currentValue}
        aria-invalid={ariaAttr(invalid)}
        aria-required={ariaAttr(required)}
        aria-readonly={ariaAttr(readOnly)}
        aria-placeholder={placeholder}
      >
        {options}
      </SelectField>
      {showChevron && (
        <ChevronIconContainer>
          <ChevronDownIcon width='1.5em' height='1.5em' />
        </ChevronIconContainer>
      )}
    </Root>
  )
})

if (__DEV__) {
  Select.displayName = 'Select'
}

const Root = styled.divBox<
  RootProps & { disabled?: boolean; $size: SelectSize }
>`
  width: 100%;
  height: fit-content;
  position: relative;
  color: black;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `};

  ${({ $size }) => {
    switch ($size) {
      case 'xs':
        return css`
          --select-padding: ${th.space(2)};
          --select-border-radius: ${th.radius('sm')};
          --select-font-size: ${th.fontSize('xs')};
          --select-line-height: ${th.lineHeight('xs')};
          --select-height-ratio: 1.2;
        `
      case 'sm':
        return css`
          --select-padding: ${th.space(3)};
          --select-border-radius: ${th.radius('sm')};
          --select-font-size: ${th.fontSize('sm')};
          --select-line-height: ${th.lineHeight('sm')};
          --select-height-ratio: 1.6;
        `
      case 'md':
        return css`
          --select-padding: ${th.space(4)};
          --select-border-radius: ${th.radius('md')};
          --select-font-size: ${th.fontSize('md')};
          --select-line-height: ${th.lineHeight('md')};
          --select-height-ratio: 2;
        `
      case 'lg':
        return css`
          --select-padding: ${th.space(4)};
          --select-border-radius: ${th.radius('md')};
          --select-font-size: ${th.fontSize('lg')};
          --select-line-height: ${th.lineHeight('lg')};
          --select-height-ratio: 2.4;
        `
      case 'xl':
        return css`
          --select-padding: ${th.space(5)};
          --select-border-radius: ${th.radius('lg')};
          --select-font-size: ${th.fontSize('xl')};
          --select-line-height: ${th.lineHeight('xl')};
          --select-height-ratio: 2.8;
        `
      default:
        return
    }
  }};
`

const StyledSelectField = styled.selectBox<SelectFieldProps>`
  cursor: pointer;
  position: relative;
  height: calc(var(--select-height-ratio) * 1.25rem);
  width: 100%;
  min-width: 0;
  padding: 0 ${th.space(8)} 1px var(--select-padding);
  outline: none;
  outline-offset: 2px;
  appearance: none;
  font-weight: normal;
  font-size: var(--select-font-size);
  line-height: var(--select-line-height);
  color: inherit;
  border-radius: var(--select-border-radius);
  border: 1px solid ${th.color('grey.l2')};
  background: white;
  transition-duration: 150ms;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  &::placeholder {
    color: ${th.color('grey.d1')};
    font-weight: normal;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:hover {
    border-color: ${th.color('grey.d2')};
  }

  &:active,
  &:focus {
    z-index: unset;
    outline: none;
    border-color: ${th.color('primary.base')};
    box-shadow: 0 0 0 1px ${th.color('primary.base')};
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;

      &:active,
      &:focus {
        border-color: ${th.color('grey.l2')};
        box-shadow: none;
      }
    `};

  ${({ invalid, disabled }) =>
    !disabled &&
    invalid &&
    css`
      border-color: error;

      &:hover {
        border-color: error;
      }

      &:active,
      &:focus {
        outline: none;
        border-color: error;
        box-shadow: 0 0 0 1px ${th.color('error')};
      }
    `};
`

const ChevronIconContainer = styled.div`
  position: absolute;
  width: 24px;
  height: 100%;
  top: 50%;
  right: calc(var(--select-padding) / 2);
  color: currentcolor;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  user-select: none;
  transform: translateY(-50%);
`
